// Service area rules — spec §1.
//
// Two constants live here and are never re-typed in a condition:
//   ACCEPT_ALL   → phase-de-lancement flag. `true` = any 5-digit ZIP passes,
//                  `false` = only ZIPs in SERVICE_AREA pass and the rest
//                  fall to the waitlist.
//   SERVICE_AREA → ZIP → city map. Used to display "Yes — we pick up in X"
//                  and to decide waitlist vs. continue when ACCEPT_ALL=false.
//
// The waitlist writes into `service_area_waitlist (email, zip)` — insert
// is open to anyone (public form), reads are admin-only.

import { supabase } from './supabase'

export const ACCEPT_ALL = true

// THE one ZIP list. It drives the entry screen's city name AND the dry
// cleaning pricing zone — there is deliberately no second list anywhere,
// because two lists always drift apart.
//
// `zone` maps to pricing_zones.key in the database. A ZIP that isn't here
// resolves to DEFAULT_ZONE, which offers no dry cleaning.
export const ZONE_SOUTH_FLORIDA = 'south_florida'
export const ZONE_PONTE_VEDRA = 'ponte_vedra'
export const DEFAULT_ZONE = 'other'

// New York is served by a local partner. Depuis le 2026-10-10, la zone
// `new_york` porte ses propres lignes item_zone_prices : la LITERIE y est
// disponible (20 articles, aux prix de base) et les VÊTEMENTS ne le sont
// pas (Men 0/7, Women 0/7). Le dry cleaning de vêtements ne tourne plus
// qu'en South Florida et à Ponte Vedra.
//
// It is a zone of its own rather than a reuse of south_florida on purpose:
// when the New York partner sets his own tariffs they get added as rows on
// THIS zone, and Miami is not touched.
export const ZONE_NEW_YORK = 'new_york'

// 130-odd New York ZIPs would bury the Florida list if they were written out
// one per line, so they are expanded from the borough arrays below. Same
// shape in the end — `SERVICE_AREA['10001']` works exactly as before.
const ny = (city, zips) =>
  Object.fromEntries(zips.map((z) => [z, { city, zone: ZONE_NEW_YORK }]))

// Tampa, ouvert le 2026-10-10. Offre complète AU POIDS — Wash & Fold,
// Same-Day, Wash & Press, Ghost Members — mais PAS de dry cleaning : les
// tarifs du contact sur place ne sont pas connus, donc la zone reste
// `other`, qui ne porte aucune ligne item_zone_prices (vérifié en base le
// 2026-10-12 : dry_cleaning = false, 0 article).
//
// Règle de Sébastien : tous les ZIP commençant par 336. Ils sont déroulés
// ici plutôt que listés à la main — un ZIP de Tampa doit afficher « Tampa »
// à l'écran, et cette liste reste la seule source de vérité.
const tampa = () => Object.fromEntries(
  Array.from({ length: 100 }, (_, i) => [
    `336${String(i).padStart(2, '0')}`,
    { city: 'Tampa', zone: DEFAULT_ZONE },
  ]),
)

// ── Queens et Long Island en entier — confirmé par Sébastien le 2026-11-04
//
// Il a de bons partenaires sur place et veut couvrir les deux zones
// complètement. Écrire ~580 codes postaux à la main serait illisible et
// faux au premier oubli, donc on les déroule par plages, exactement comme
// Tampa plus haut.
//
// ⚠️ Ces plages sont posées AVANT les villes nommées plus bas. L'ordre
// compte : une ville nommée réécrit la plage et garde son étiquette, donc
// Syosset affiche « Syosset » et pas « Long Island ».
//
// Certaines valeurs de ces plages ne correspondent à aucun code postal
// réel. C'est sans effet : elles ne seront jamais saisies.
const zipRange = (from, to, city) => Object.fromEntries(
  Array.from({ length: to - from + 1 }, (_, i) => [
    String(from + i).padStart(5, '0'),
    { city, zone: ZONE_NEW_YORK },
  ]),
)

// Nassau + Suffolk. 11001-11099 recouvre deux ZIP du Queens (11004,
// 11005) — le bloc Queens juste après les reprend.
const longIslandAll = () => ({
  ...zipRange(11001, 11099, 'Long Island'),
  ...zipRange(11501, 11599, 'Long Island'),
  ...zipRange(11701, 11999, 'Long Island'),
})

// Les villes principales de Long Island, nommées pour l'écran d'entrée.
//
// Sans elles, un client de Hempstead lit « Yes — we pick up in Long
// Island » : une région, pas une ville, alors que tous les autres codes du
// fichier affichent un vrai lieu.
//
// Elles ne changent AUCUNE couverture et AUCUNE zone : les plages
// ci-dessus servent déjà toute l'île en `new_york`. Seule l'étiquette
// change. Un nom faux est donc un défaut d'affichage, jamais un refus de
// service.
//
// ⚠️ Ces correspondances viennent de la connaissance générale, pas d'un
// fichier USPS. À corriger ligne par ligne si l'une est fausse — et les
// codes non listés continuent d'afficher « Long Island », ce qui reste
// vrai.
const LONG_ISLAND_TOWNS = {
  // ── Nassau ──
  '11001': 'Floral Park',
  '11003': 'Elmont',
  '11010': 'Franklin Square',
  '11030': 'Manhasset',
  '11040': 'New Hyde Park',
  '11050': 'Port Washington',
  '11501': 'Mineola',
  '11510': 'Baldwin',
  '11516': 'Cedarhurst',
  '11520': 'Freeport',
  '11530': 'Garden City',
  '11542': 'Glen Cove',
  '11550': 'Hempstead',
  '11553': 'Uniondale',
  '11554': 'East Meadow',
  '11561': 'Long Beach',
  '11563': 'Lynbrook',
  '11566': 'Merrick',
  '11570': 'Rockville Centre',
  '11572': 'Oceanside',
  '11576': 'Roslyn',
  '11580': 'Valley Stream',
  '11590': 'Westbury',
  '11598': 'Woodmere',
  // ── Suffolk ──
  '11701': 'Amityville',
  '11706': 'Bay Shore',
  '11717': 'Brentwood',
  '11722': 'Central Islip',
  '11729': 'Deer Park',
  '11743': 'Huntington',
  '11746': 'Huntington Station',
  '11751': 'Islip',
  '11754': 'Kings Park',
  '11757': 'Lindenhurst',
  '11768': 'Northport',
  '11772': 'Patchogue',
  '11777': 'Port Jefferson',
  '11779': 'Ronkonkoma',
  '11782': 'Sayville',
  '11784': 'Selden',
  '11787': 'Smithtown',
  '11788': 'Hauppauge',
  '11790': 'Stony Brook',
  '11795': 'West Islip',
  '11901': 'Riverhead',
  '11937': 'East Hampton',
  '11946': 'Hampton Bays',
  '11954': 'Montauk',
  '11963': 'Sag Harbor',
  '11964': 'Shelter Island',
  '11968': 'Southampton',
  '11971': 'Southold',
  '11978': 'Westhampton Beach',
}

const longIslandNamed = () => Object.fromEntries(
  Object.entries(LONG_ISLAND_TOWNS).map(([z, city]) => [z, { city, zone: ZONE_NEW_YORK }]),
)

const queensAll = () => ({
  ...zipRange(11004, 11005, 'Queens'),
  ...zipRange(11101, 11106, 'Queens'),
  ...zipRange(11351, 11390, 'Queens'),
  ...zipRange(11411, 11436, 'Queens'),
  ...zipRange(11691, 11697, 'Queens'),
})

export const SERVICE_AREA = {
  // ── South Florida — Delray Beach down to Homestead ────────────────
  // Miami-Dade
  '33131': { city: 'Brickell', zone: ZONE_SOUTH_FLORIDA },
  '33132': { city: 'Downtown Miami', zone: ZONE_SOUTH_FLORIDA },
  '33137': { city: 'Edgewater', zone: ZONE_SOUTH_FLORIDA },
  '33139': { city: 'Miami Beach', zone: ZONE_SOUTH_FLORIDA },
  '33140': { city: 'Mid-Beach', zone: ZONE_SOUTH_FLORIDA },
  '33141': { city: 'North Beach', zone: ZONE_SOUTH_FLORIDA },
  '33154': { city: 'Bal Harbour', zone: ZONE_SOUTH_FLORIDA },
  '33160': { city: 'Sunny Isles', zone: ZONE_SOUTH_FLORIDA },
  '33180': { city: 'Aventura', zone: ZONE_SOUTH_FLORIDA },
  '33181': { city: 'North Miami', zone: ZONE_SOUTH_FLORIDA },
  '33161': { city: 'North Miami Beach', zone: ZONE_SOUTH_FLORIDA },
  // Doral was advertised with these three ZIPs on /dry-cleaning-miami long
  // before it had a city page, but it was never in this list — so the app
  // offered no dry cleaning to an address the site said we dry-cleaned.
  '33122': { city: 'Doral', zone: ZONE_SOUTH_FLORIDA },
  '33172': { city: 'Doral', zone: ZONE_SOUTH_FLORIDA },
  '33178': { city: 'Doral', zone: ZONE_SOUTH_FLORIDA },
  // Broward
  '33301': { city: 'Fort Lauderdale', zone: ZONE_SOUTH_FLORIDA },
  '33304': { city: 'Victoria Park', zone: ZONE_SOUTH_FLORIDA },
  '33305': { city: 'Wilton Manors', zone: ZONE_SOUTH_FLORIDA },
  '33306': { city: 'Oakland Park', zone: ZONE_SOUTH_FLORIDA },
  '33308': { city: 'Lauderdale-by-the-Sea', zone: ZONE_SOUTH_FLORIDA },
  '33316': { city: 'Harbor Beach', zone: ZONE_SOUTH_FLORIDA },
  '33334': { city: 'Oakland Park', zone: ZONE_SOUTH_FLORIDA },
  '33062': { city: 'Pompano Beach', zone: ZONE_SOUTH_FLORIDA },
  '33441': { city: 'Deerfield Beach', zone: ZONE_SOUTH_FLORIDA },
  '33442': { city: 'Deerfield Beach', zone: ZONE_SOUTH_FLORIDA },
  '33009': { city: 'Hallandale Beach', zone: ZONE_SOUTH_FLORIDA },
  '33019': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  '33020': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  '33021': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  '33023': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  // 33024, 33025 and 33026 straddle Hollywood and Pembroke Pines in real
  // life. They stay labelled Hollywood here (Sébastien's call, 2026-10-08) —
  // the marketing site lists them on the Pembroke Pines page too, which is
  // correct: that is where someone on those streets will look.
  '33024': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  '33025': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  '33026': { city: 'Hollywood', zone: ZONE_SOUTH_FLORIDA },
  '33027': { city: 'Pembroke Pines', zone: ZONE_SOUTH_FLORIDA },
  '33028': { city: 'Pembroke Pines', zone: ZONE_SOUTH_FLORIDA },
  '33029': { city: 'Pembroke Pines', zone: ZONE_SOUTH_FLORIDA },
  '33432': { city: 'Boca Raton', zone: ZONE_SOUTH_FLORIDA },
  '33433': { city: 'Boca Raton', zone: ZONE_SOUTH_FLORIDA },
  '33486': { city: 'Boca Raton', zone: ZONE_SOUTH_FLORIDA },
  '33487': { city: 'Boca Raton', zone: ZONE_SOUTH_FLORIDA },
  // Palm Beach County — Delray Beach is the NORTHERN LIMIT of the dry
  // cleaning zone. Boynton Beach and West Palm Beach are deliberately
  // absent: still served for Wash & Fold, no dry cleaning.
  '33444': { city: 'Delray Beach', zone: ZONE_SOUTH_FLORIDA },
  '33445': { city: 'Delray Beach', zone: ZONE_SOUTH_FLORIDA },
  '33483': { city: 'Delray Beach', zone: ZONE_SOUTH_FLORIDA },

  // ── Ponte Vedra — Ponte Vedra Beach + 15 miles ────────────────────
  // Jacksonville Beach 3.8 mi · Nocatee 8.9 · Jax Southside 10.4.
  // Downtown Jacksonville (17.3), Orange Park (19.8) and St. Augustine
  // (23.7) are OUT, so their ZIPs are absent on purpose.
  '32082': { city: 'Ponte Vedra Beach', zone: ZONE_PONTE_VEDRA },
  '32081': { city: 'Nocatee', zone: ZONE_PONTE_VEDRA },
  '32250': { city: 'Jacksonville Beach', zone: ZONE_PONTE_VEDRA },
  '32266': { city: 'Neptune Beach', zone: ZONE_PONTE_VEDRA },
  '32233': { city: 'Atlantic Beach', zone: ZONE_PONTE_VEDRA },
  '32256': { city: 'Jacksonville Southside', zone: ZONE_PONTE_VEDRA },
  '32246': { city: 'Jacksonville Southside', zone: ZONE_PONTE_VEDRA },

  // ── Greater New York — served by a local partner ──────────────────
  // These must stay identical to src/data/cities.js in the ghost-laundry
  // repo: the marketing site publishes them as a coverage promise, and the
  // two lists disagreeing is how a customer gets told "yes" on one screen
  // and "we don't cover you" on the next.
  // Plages d'abord, villes nommées ensuite — voir le commentaire plus haut.
  ...longIslandAll(),
  ...queensAll(),
  // Après les plages, avant les villes nommées une par une plus bas :
  // Syosset, Hicksville et les autres doivent rester prioritaires sur
  // cette table.
  ...longIslandNamed(),

  ...ny('Manhattan', [
    '10001', '10002', '10003', '10004', '10005', '10006', '10007', '10009',
    '10010', '10011', '10012', '10013', '10014', '10016', '10017', '10018',
    '10019', '10021', '10022', '10023', '10024', '10025', '10026', '10027',
    '10028', '10029', '10030', '10031', '10032', '10033', '10034', '10035',
    '10036', '10037', '10038', '10039', '10040', '10044', '10065', '10069',
    '10075', '10128', '10280', '10282',
  ]),
  ...ny('Bronx', [
    '10451', '10452', '10453', '10454', '10455', '10456', '10457', '10458',
    '10459', '10460', '10461', '10462', '10463', '10464', '10465', '10466',
    '10467', '10468', '10469', '10470', '10471', '10472', '10473', '10474',
    '10475',
  ]),
  ...ny('Brooklyn', [
    '11201', '11203', '11204', '11205', '11206', '11207', '11208', '11209',
    '11210', '11211', '11212', '11213', '11214', '11215', '11216', '11217',
    '11218', '11219', '11220', '11221', '11222', '11223', '11224', '11225',
    '11226', '11228', '11229', '11230', '11231', '11232', '11233', '11234',
    '11235', '11236', '11237', '11238', '11239',
  ]),
  ...ny('Queens', [
    '11101', '11102', '11103', '11104', '11105', '11106', '11354', '11355',
    '11356', '11357', '11358', '11360', '11361', '11362', '11363', '11364',
    '11365', '11366', '11367',
    '11368', '11369', '11370', '11372', '11373', '11374', '11375', '11377',
    '11378', '11379', '11385',
  ]),
  // ── Comté de Nassau ───────────────────────────────────────────────
  // Syosset était seule ici. Les six suivantes sont ajoutées le
  // 2026-10-12 après confirmation de Sébastien que le partenaire les
  // livre. Un ZIP par ville : Massapequa Park (11762) n'en fait PAS
  // partie, c'est un code distinct et personne ne l'a confirmé.
  ...ny('Syosset', ['11791']),
  // Great Neck confirmée par Sébastien le 2026-11-04. Il y avait déjà un
  // vrai client au 11021 qui passait uniquement grâce à ACCEPT_ALL.
  // 11020 ajouté le 2026-11-04 : il tombait dans la plage et s'affichait
  // « Long Island ». 11022 n'y est pas, c'est une boîte postale.
  ...ny('Great Neck', ['11020', '11021', '11023', '11024']),
  ...ny('Hicksville', ['11801']),
  ...ny('Plainview', ['11803']),
  ...ny('Jericho', ['11753']),
  ...ny('Woodbury', ['11797']),
  ...ny('Levittown', ['11756']),
  ...ny('Massapequa', ['11758']),

  // ── Tampa — tout le 336xx, au poids seulement ─────────────────────
  ...tampa(),
}

/**
 * Pricing zone for a ZIP. An unknown ZIP gets DEFAULT_ZONE — never an
 * error, and never dry cleaning.
 * @param {string} raw
 * @returns {string} a pricing_zones.key
 */
export function zoneForZip(raw) {
  const zip = String(raw || '').replace(/\D/g, '').slice(0, 5)
  return SERVICE_AREA[zip]?.zone || DEFAULT_ZONE
}

const ZIP_REGEX = /^\d{5}$/

/**
 * Evaluate a raw ZIP string against the service-area rules.
 * @param {string} raw
 * @returns {{ valid: boolean, accepted: boolean, city: string, zip: string }}
 */
export function checkZip(raw) {
  const zip = String(raw || '').replace(/\D/g, '').slice(0, 5)
  if (!ZIP_REGEX.test(zip)) return { valid: false, accepted: false, city: '', zip }
  const city = SERVICE_AREA[zip]?.city || ''
  const accepted = ACCEPT_ALL || Object.prototype.hasOwnProperty.call(SERVICE_AREA, zip)
  return { valid: true, accepted, city, zip }
}

/**
 * Insert into the waitlist. Idempotent-friendly: the DB should have a
 * unique constraint on (email, zip) — the caller ignores duplicate errors
 * silently. Returns true on success (or already-existing row), false on
 * real failure.
 * @param {string} email
 * @param {string} zip
 */
export async function joinWaitlist(email, zip) {
  const em = String(email || '').trim().toLowerCase()
  const z = String(zip || '').trim()
  if (!em || !z) return false
  const { error } = await supabase
    .from('service_area_waitlist')
    .insert({ email: em, zip: z })
  if (error) {
    // 23505 = unique_violation — already on the list, count as success.
    if (error.code === '23505') return true
    // eslint-disable-next-line no-console
    console.warn('[waitlist] insert failed', error)
    return false
  }
  return true
}
